import { useEffect, useState, useMemo } from "react";
import { Link } from "react-router-dom";
import { Check, Minus, X, Plus, Search } from "lucide-react";
import api from "@/lib/api";
import { formatINR } from "@/lib/utils-format";
import Seo from "@/components/Seo";
import CompareTable from "@/components/sections/CompareTable";

const TIER_RANK = {
    "Essential": 1, "Essential CROS": 1,
    "Standard": 2, "Standard CROS": 2,
    "Advanced": 3, "Advanced CROS": 3,
    "Premium": 4, "Premium CROS": 4,
    "Flagship": 5, "Flagship CROS": 5,
};

const CHANNELS_BY_TIER = {
    "Essential": 16, "Essential CROS": 16,
    "Standard": 24, "Standard CROS": 24,
    "Advanced": 32, "Advanced CROS": 32,
    "Premium": 36, "Premium CROS": 36,
    "Flagship": 48, "Flagship CROS": 48,
};

const warrantyYearsFor = (p) => {
    const s = (p?.slug || "").toLowerCase();
    const t = (p?.technology_level || "").toLowerCase();
    if (s.includes("5ix") || s.includes("7ix") || t.includes("premium") || t.includes("flagship")) return 4;
    return 3;
};

const tierStars = (tier) => {
    const r = TIER_RANK[tier] || 1;
    return "●".repeat(r) + "○".repeat(5 - r);
};

const ROWS = [
    { key: "tier", label: "Technology Tier" },
    { key: "channels", label: "Sound Channels" },
    { key: "noise", label: "Speech-in-Noise" },
    { key: "bluetooth", label: "Bluetooth Streaming" },
    { key: "rechargeable", label: "Rechargeable" },
    { key: "app", label: "Signia App" },
    { key: "cros", label: "CROS (Single-Sided)" },
    { key: "warranty", label: "Warranty" },
    { key: "ideal", label: "Ideal For" },
    { key: "price", label: "Price (per pair)" },
    { key: "emi", label: "EMI / month (12mo)" },
];

const cellFor = (key, p) => {
    switch (key) {
        case "tier":
        case "noise":
            return <span className="font-semibold text-[#003E4D]">{tierStars(p.technology_level)}</span>;
        case "channels":
            return <span>{CHANNELS_BY_TIER[p.technology_level] || 16} ch</span>;
        case "bluetooth":
        case "rechargeable":
        case "app":
            return <Check size={16} className="text-[#003E4D]" />;
        case "cros":
            return p.is_cros ? <Check size={16} className="text-[#003E4D]" /> : <Minus size={16} className="text-[#9CA3AF]" />;
        case "warranty":
            return <span>{warrantyYearsFor(p)}-year Signia India</span>;
        case "ideal":
            return <span className="text-[13px]">{p.suitable_for}</span>;
        case "price":
            return <span className="font-semibold">{formatINR(p.price)}</span>;
        case "emi":
            return <span>{p.emi_per_month ? formatINR(p.emi_per_month) : "—"}</span>;
        default:
            return null;
    }
};

// Canonical shopping order for the IX line
const CORE_IX_ORDER = [
    "signia-styletto-1ix",
    "kit-styletto-1ix",
    "kit-styletto-2ix",
    "kit-styletto-3ix",
    "kit-styletto-4ix",
    "kit-styletto-5ix",
    "kit-styletto-7ix",
];

export default function Compare() {
    const [products, setProducts] = useState([]);
    const [mode, setMode] = useState("full"); // "full" | "pick"
    const [picked, setPicked] = useState([]); // slugs
    const [pickerOpenSlot, setPickerOpenSlot] = useState(null); // 0/1/2 or null
    const [searchQ, setSearchQ] = useState("");

    useEffect(() => {
        document.title = "Compare Signia Styletto IX Models | Pick 2 or 3 to Compare";
        api.get("/products").then((r) => setProducts(r.data)).catch(() => {});
    }, []);

    // Available IX products only, sorted by canonical order
    const ixProducts = useMemo(() => {
        const bySlug = Object.fromEntries(products.map((p) => [p.slug, p]));
        const ordered = CORE_IX_ORDER.map((s) => bySlug[s]).filter(Boolean);
        const rest = products.filter(
            (p) => /ix/i.test(p.slug) && !CORE_IX_ORDER.includes(p.slug),
        );
        return [...ordered, ...rest];
    }, [products]);

    // Initialise picker with 2 sensible defaults once products load
    useEffect(() => {
        if (picked.length === 0 && ixProducts.length >= 2) {
            const d1 = ixProducts.find((p) => p.slug.includes("3ix")) || ixProducts[0];
            const d2 = ixProducts.find((p) => p.slug.includes("7ix")) || ixProducts[1];
            setPicked([d1.slug, d2.slug]);
        }
    }, [ixProducts, picked.length]);

    const pickedProducts = picked.map((s) => ixProducts.find((p) => p.slug === s)).filter(Boolean);
    const canAdd = picked.length < 3;

    const openPickerFor = (slotIndex) => {
        setPickerOpenSlot(slotIndex);
        setSearchQ("");
    };

    const selectSlot = (slug) => {
        const next = [...picked];
        if (pickerOpenSlot === next.length) {
            // adding a new slot
            next.push(slug);
        } else {
            next[pickerOpenSlot] = slug;
        }
        setPicked(next);
        setPickerOpenSlot(null);
    };

    const removeSlot = (slotIndex) => {
        if (picked.length <= 2) return; // keep min 2
        setPicked(picked.filter((_, i) => i !== slotIndex));
    };

    const filteredPickList = ixProducts.filter((p) => {
        if (picked.includes(p.slug)) return false;
        const q = searchQ.trim().toLowerCase();
        if (!q) return true;
        return (
            (p.name || "").toLowerCase().includes(q) ||
            (p.short_name || "").toLowerCase().includes(q) ||
            (p.technology_level || "").toLowerCase().includes(q) ||
            (p.slug || "").toLowerCase().includes(q)
        );
    });

    return (
        <>
            <Seo
                title="Compare Signia Styletto IX Models — Pick 2 or 3 to Compare Side-by-Side"
                description="Interactive side-by-side comparison of Signia Styletto 1IX, 2IX, 3IX, 5IX & 7IX. Pick any 2 or 3 models to compare price, tier, channels, features, warranty and EMI."
                canonical="https://signiastylettoix.com/compare"
            />

            <section className="pt-24 pb-6 md:pt-36 md:pb-12">
                <div className="container-x max-w-[880px]">
                    <p className="eyebrow">Compare Styletto IX</p>
                    <h1 className="font-serif text-[44px] md:text-[64px] leading-[1.04] mt-3">
                        Side-by-side. <span className="italic text-[#003E4D]">Pick your perfect tier.</span>
                    </h1>
                    <p className="mt-5 text-[16px] text-[#4a5568] max-w-[640px] leading-relaxed">
                        Compare the full Signia Styletto IX range from <b>1IX</b> through <b>7IX</b>, or pick any 2 or 3 models
                        for a focused side-by-side.
                    </p>
                </div>
            </section>

            {/* Mode toggle */}
            <section className="pb-8">
                <div className="container-x flex items-center gap-2">
                    <button
                        onClick={() => setMode("full")}
                        data-testid="compare-mode-full"
                        className={`px-5 py-2.5 rounded-full text-[13px] font-medium transition border-2 ${mode === "full" ? "bg-[#003E4D] text-white border-[#003E4D]" : "bg-white border-[#E5E7EB] text-[#4a5568] hover:border-[#003E4D]"}`}
                    >
                        Full comparison (all IX tiers)
                    </button>
                    <button
                        onClick={() => setMode("pick")}
                        data-testid="compare-mode-pick"
                        className={`px-5 py-2.5 rounded-full text-[13px] font-medium transition border-2 ${mode === "pick" ? "bg-[#003E4D] text-white border-[#003E4D]" : "bg-white border-[#E5E7EB] text-[#4a5568] hover:border-[#003E4D]"}`}
                    >
                        Pick 2 or 3 to compare
                    </button>
                </div>
            </section>

            {mode === "full" ? (
                <CompareTable />
            ) : (
                <section className="pb-16 bg-[#f8f9fa]" data-testid="compare-picker-section">
                    <div className="container-x pt-10">
                        {/* Slot selector */}
                        <div className="grid grid-cols-2 md:grid-cols-3 gap-3 md:gap-4 mb-8" data-testid="compare-slots">
                            {[0, 1, 2].map((slot) => {
                                if (slot < pickedProducts.length) {
                                    const p = pickedProducts[slot];
                                    return (
                                        <div key={slot} className="relative bg-white rounded-2xl border border-[#003E4D]/15 p-4 flex items-center gap-3" data-testid={`compare-slot-${slot}`}>
                                            <img src={p.image} alt={p.name} className="w-14 h-14 object-contain bg-[#f3f4f6] rounded-lg" />
                                            <div className="flex-1 min-w-0">
                                                <p className="text-[11px] uppercase tracking-[0.16em] text-[#003E4D] font-semibold">{p.technology_level}</p>
                                                <p className="text-[14px] font-serif truncate">{p.short_name || p.name}</p>
                                                <p className="text-[12.5px] text-[#4a5568]">{formatINR(p.price)}</p>
                                            </div>
                                            <div className="flex flex-col gap-1">
                                                <button
                                                    onClick={() => openPickerFor(slot)}
                                                    className="text-[11px] text-[#003E4D] hover:underline whitespace-nowrap"
                                                    data-testid={`compare-slot-change-${slot}`}
                                                >
                                                    Change
                                                </button>
                                                {picked.length > 2 && (
                                                    <button
                                                        onClick={() => removeSlot(slot)}
                                                        className="text-[11px] text-[#9CA3AF] hover:text-red-500 inline-flex items-center gap-1"
                                                        data-testid={`compare-slot-remove-${slot}`}
                                                    >
                                                        <X size={11} /> Remove
                                                    </button>
                                                )}
                                            </div>
                                        </div>
                                    );
                                }
                                // empty add-slot
                                if (slot === pickedProducts.length && canAdd) {
                                    return (
                                        <button
                                            key={slot}
                                            onClick={() => openPickerFor(slot)}
                                            className="rounded-2xl border-2 border-dashed border-[#003E4D]/30 hover:border-[#003E4D] hover:bg-[#003E4D]/[0.03] p-6 flex flex-col items-center justify-center text-[#003E4D] transition min-h-[92px]"
                                            data-testid={`compare-slot-add-${slot}`}
                                        >
                                            <Plus size={20} />
                                            <span className="text-[13px] font-medium mt-1">Add 3rd model</span>
                                        </button>
                                    );
                                }
                                return <div key={slot} className="hidden md:block" />;
                            })}
                        </div>

                        {/* Comparison table (of picked) */}
                        <div className="overflow-x-auto bg-white rounded-3xl border border-black/5 no-scrollbar">
                            <table className="w-full min-w-[600px] text-left" data-testid="compare-picker-table">
                                <thead>
                                    <tr className="border-b border-[#E5E7EB]">
                                        <th className="p-5 text-[12px] font-semibold uppercase tracking-[0.18em] text-[#4a5568] w-[200px]">Feature</th>
                                        {pickedProducts.map((p) => (
                                            <th key={p.slug} className="p-5 text-left">
                                                <div className="font-serif text-[19px] text-[#1a1a1a]">{p.short_name || p.name}</div>
                                                <div className="text-[12px] text-[#4a5568] font-normal mt-0.5">{p.technology_level}</div>
                                            </th>
                                        ))}
                                    </tr>
                                </thead>
                                <tbody>
                                    {ROWS.map((r) => (
                                        <tr key={r.key} className="border-b border-[#E5E7EB] last:border-0">
                                            <td className="p-5 text-[13px] text-[#4a5568] font-medium">{r.label}</td>
                                            {pickedProducts.map((p) => (
                                                <td key={p.slug} className="p-5 text-[13.5px] text-[#1a1a1a]">
                                                    {cellFor(r.key, p)}
                                                </td>
                                            ))}
                                        </tr>
                                    ))}
                                    <tr className="last:border-0">
                                        <td className="p-5"></td>
                                        {pickedProducts.map((p) => (
                                            <td key={p.slug} className="p-5">
                                                <Link
                                                    to={`/products/${p.slug}`}
                                                    className="inline-flex items-center gap-1.5 text-[12.5px] px-3.5 py-2 rounded-full bg-[#003E4D] text-white hover:bg-[#00293b] transition"
                                                    data-testid={`compare-view-${p.slug}`}
                                                >
                                                    View {p.short_name}
                                                </Link>
                                            </td>
                                        ))}
                                    </tr>
                                </tbody>
                            </table>
                        </div>
                    </div>

                    {/* Picker modal */}
                    {pickerOpenSlot !== null && (
                        <div className="fixed inset-0 z-50 bg-black/50 backdrop-blur-sm flex items-center justify-center p-4" onClick={() => setPickerOpenSlot(null)} data-testid="compare-picker-modal">
                            <div className="bg-white rounded-3xl max-w-[560px] w-full max-h-[80vh] flex flex-col overflow-hidden" onClick={(e) => e.stopPropagation()}>
                                <div className="p-5 border-b border-[#E5E7EB] flex items-center gap-3">
                                    <Search size={16} className="text-[#4a5568]" />
                                    <input
                                        autoFocus
                                        value={searchQ}
                                        onChange={(e) => setSearchQ(e.target.value)}
                                        placeholder="Search Styletto IX models (1IX, 3IX, 7IX…)"
                                        className="flex-1 h-9 focus:outline-none text-[14px]"
                                        data-testid="compare-picker-search"
                                    />
                                    <button onClick={() => setPickerOpenSlot(null)} className="text-[#9CA3AF] hover:text-black" data-testid="compare-picker-close">
                                        <X size={18} />
                                    </button>
                                </div>
                                <div className="overflow-y-auto flex-1">
                                    {filteredPickList.map((p) => (
                                        <button
                                            key={p.slug}
                                            onClick={() => selectSlot(p.slug)}
                                            className="w-full text-left p-4 flex items-center gap-3 hover:bg-[#f8f9fa] border-b border-[#E5E7EB] last:border-0 transition"
                                            data-testid={`compare-picker-option-${p.slug}`}
                                        >
                                            <img src={p.image} alt={p.name} className="w-12 h-12 object-contain bg-[#f3f4f6] rounded-lg" />
                                            <div className="flex-1 min-w-0">
                                                <p className="text-[11px] uppercase tracking-[0.16em] text-[#003E4D] font-semibold">{p.technology_level}</p>
                                                <p className="text-[14px] font-medium truncate">{p.short_name || p.name}</p>
                                            </div>
                                            <span className="text-[13px] font-semibold text-[#003E4D]">{formatINR(p.price)}</span>
                                        </button>
                                    ))}
                                    {filteredPickList.length === 0 && (
                                        <p className="p-10 text-center text-[#9CA3AF] text-[14px]">No matching models.</p>
                                    )}
                                </div>
                            </div>
                        </div>
                    )}
                </section>
            )}
        </>
    );
}
